import React, { FC, Fragment, memo, useMemo, useCallback, useContext, useRef } from 'react'

import HeaderSection from '../common/header-section'
import CommonHeroSection from '../common/hero-section'
import BestDestinationsSection from '../region-landing-page/best-destinations-section'
import VacationRegionsSection from '../region-landing-page/vacation-regions-section'
import TipsSection from '../region-landing-page/tips-section'
import QuoteSection from '../region-landing-page/quote-section'
import RegionOverviewSection from '../region-landing-page/region-overview'
import FAQSection from '../region-landing-page/faqs-section'
import TrustBar from '../common/trust-bar-section'
import ReviewContentSection from '../region-landing-page/review-content-section'
import AccommodationSection from '../region-landing-page/accommodation-section'
import ReviewsSection from '../region-landing-page/reviews-section'
import TopOffersSection from '../region-landing-page/top-offers-section'
import HolidayIdeasSection from '../region-landing-page/holiday-ideas-section'
import KeyInformationSection from '../region-landing-page/key-information-section'
import PersonInterlinksSection from '../region-landing-page/person-interlinks-section'
import NewsLetter from '../common/news-letter-section'
import FooterSection from '../common/footer'
import BreadcrumbSection from './breadcrumb-section'
import TopRecommendationsSection from '../region-landing-page/top-recommendations-section'
import {
    HeroSectionTransport,
    HolidayIdeasSectionTransport,
    PopularDestinationsSectionTransport,
    PopularFiltersTransport,
    RegionLandingPageStaticTransport,
    RegionsSectionTransport,
    TypicalAccommodationsSectionTransport,
    TemplateDataTransport,
} from '../../transports/region-landing-page-transport'
import ListingSectionTransport from '../../transports/common/listing-section-transport'
import { Container, InnerContainer, GYGWrapper } from './landing-page-styles'
import { ScreenContext } from 'react-components'
import { useScrollToSearch } from '../../hooks/scroll-to-search'
import PopularObjectSection from '../region-landing-page/popular-objects-section'
import PopularFilterListingsSection from '../region-landing-page/popular-filter-listings-section'
import { getOrganizationSchemaDetail } from '../../schema/common'
import { BaseSearchbarHistoryItem } from '../../redux/searchbar/types'
import { CallbackFunction } from '../../common/types'
import { useTranslation } from '../../hooks/locale'
import { useABVariant } from '../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../common/constants'
import { RegionDirectoryPageTransport } from '../../transports/directory-transport'
import DirectorySection from '../region-landing-page/directory-section'
import GYGExperiencesSection from '../common/gyg-experiences-section'
import config from '../../common/config'
import StickyFooter from './sticky-footer'
import { BreadcrumbList } from '../../models/common/breadcrumb-model'
import { Offer } from '../../models/common/offers-model'
import { SuitablesModel } from '../../models/common/suitables-model'
import { useDateFormatter } from '../../hooks/date'
import AdvancedBreadcrumbTransport from '../../transports/common/advanced-breadcrumb-transport'
import MegaBar from '../common/megabar'
import TrustBarTransport from '../../transports/common/trustbar-transport'
import { BackgroundImages } from '../common/hero-section/styles'
import PageSubTypeTransport from '../../transports/common/booking-types-transport'

interface LandingPageProps {
    isLoggedIn: boolean
    hero: HeroSectionTransport
    recommendedObjects: ListingSectionTransport | null
    pageSubTypeResponse?: PageSubTypeTransport
    staticData: RegionLandingPageStaticTransport
    templateData: TemplateDataTransport | null
    popularDestinationsSection: PopularDestinationsSectionTransport | null
    topRatedObjectsSection: ListingSectionTransport | null
    topOfferListingsSection: ListingSectionTransport | null
    typicalAccommodationsSection: TypicalAccommodationsSectionTransport | null
    popularFilterListingsSections: ListingSectionTransport[]
    popularFiltersSection: PopularFiltersTransport | null
    regionsSection: RegionsSectionTransport | null
    holidayIdeasSection: HolidayIdeasSectionTransport | null
    locationName: string
    addToHistory: (historyItem: BaseSearchbarHistoryItem) => void
    searchFunction: CallbackFunction
    firstName?: string
    url: string
    breadcrumbs: AdvancedBreadcrumbTransport[]
    directoryPageResponse: RegionDirectoryPageTransport | null
    trustBar: TrustBarTransport
    heroBackgroundImages: BackgroundImages
}

const LandingPage: FC<LandingPageProps> = ({
    isLoggedIn,
    hero,
    recommendedObjects,
    pageSubTypeResponse,
    staticData,
    templateData,
    popularDestinationsSection,
    topRatedObjectsSection,
    topOfferListingsSection,
    typicalAccommodationsSection,
    popularFilterListingsSections,
    popularFiltersSection,
    regionsSection,
    holidayIdeasSection,
    locationName,
    firstName,
    url,
    breadcrumbs,
    directoryPageResponse,
    trustBar,
    heroBackgroundImages,
}) => {
    const { isMobile } = useContext(ScreenContext)
    const searchbarRef = useRef<HTMLDivElement>(null)
    const showHeaderSearch = useScrollToSearch(searchbarRef as React.RefObject<HTMLDivElement>, isMobile)

    const scrollToSearchbar = useCallback(() => {
        if (searchbarRef.current) {
            searchbarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }, [])

    const {
        headerSection,
        newsLetterSection,
        footerSection,
        tipsSection,
        quoteSection,
        faqs,
        regionOverviewSection,
        heroSectionForRegion,
        cmsPopularDestinationsSection,
        topRecommendationsSection,
        reviewContentSection,
        megabar,
    } = staticData

    const { t } = useTranslation()
    const { isProvidedVariantActive: isGYGEnabled } = useABVariant(AB_KEY.EXPERIENCES_SECTION, AB_VARIANT_VERSION.ONE)

    const organizationSchemaHtml = useMemo(
        () => getOrganizationSchemaDetail(t, topRatedObjectsSection ?? undefined),
        [t, topRatedObjectsSection],
    )

    const hasDirectorySectionWithLinks = directoryPageResponse?.sections?.some(section => section.links.length > 0)
    const { format } = useDateFormatter()
    const breadcrumbList = useMemo(() => new BreadcrumbList(t), [t])
    const breadcrumbListModel = breadcrumbList
    const offerModel = useMemo(() => new Offer(t, format), [t, format])
    const suitablesModel = useMemo(() => new SuitablesModel(t), [t])

    return (
        <Fragment>
            <script type="application/ld+json" dangerouslySetInnerHTML={organizationSchemaHtml} />
            <HeaderSection
                firstName={firstName}
                isLoggedIn={isLoggedIn}
                url={url}
                headerSection={headerSection}
                showVacationIdeasNavButton
                megabar={megabar}
                onSearchIconClick={showHeaderSearch ? scrollToSearchbar : undefined}
            />
            <main id="region-landing-page-main">
                <Container>
                    {megabar && <MegaBar megabar={megabar} />}
                    <CommonHeroSection
                        ref={searchbarRef}
                        title={
                            heroSectionForRegion && heroSectionForRegion.heading
                                ? heroSectionForRegion.heading
                                : hero.description.title && hero.description.subtitle
                                  ? `${hero.description.title} ${hero.description.subtitle}`
                                  : hero.description.title || hero.description.subtitle
                        }
                        subtitle={`${t('heroSubtitle')} ${hero?.totalCount ?? 0} ${t('vacationRentals')}`}
                        backgroundImages={heroBackgroundImages}
                        logo={headerSection.navigationSectionResponse?.navigation?.logo}
                        textColor="white"
                        hasOverlay
                        initialLocation={locationName}
                        isBorderGradientEnabled
                        showSliderSection
                        pageSubTypeResponse={pageSubTypeResponse}
                    />
                    <InnerContainer>
                        <TrustBar trustBar={trustBar} />
                        {!!breadcrumbs && breadcrumbs?.length > 0 && (
                            <BreadcrumbSection breadcrumbs={breadcrumbList.format(breadcrumbs, '')} url={url} />
                        )}
                        {!!popularDestinationsSection &&
                            popularDestinationsSection?.popularDestinations?.length > 0 && (
                                <BestDestinationsSection
                                    popularDestinationsSection={popularDestinationsSection}
                                    cmsPopularDestinationsSection={cmsPopularDestinationsSection}
                                />
                            )}
                        {!!recommendedObjects && recommendedObjects?.objects?.length > 0 && (
                            <TopRecommendationsSection
                                topRecommendedSection={recommendedObjects}
                                staticTopRecommendedSection={topRecommendationsSection}
                                url={url}
                                locationName={locationName}
                                offerHeading={topOfferListingsSection?.heading}
                                breadcrumbModel={breadcrumbListModel}
                                offerModel={offerModel}
                                suitablesModel={suitablesModel}
                            />
                        )}
                        {!!topRatedObjectsSection && topRatedObjectsSection?.objects?.length > 0 && (
                            <ReviewsSection
                                topRatedObjectsSection={topRatedObjectsSection}
                                breadcrumbList={breadcrumbList}
                            />
                        )}
                        {!!topOfferListingsSection && topOfferListingsSection?.objects?.length > 0 && (
                            <TopOffersSection
                                topOfferListingsSection={topOfferListingsSection}
                                url={url}
                                breadcrumbModel={breadcrumbListModel}
                                offerModel={offerModel}
                                suitablesModel={suitablesModel}
                            />
                        )}
                        {!!typicalAccommodationsSection && typicalAccommodationsSection?.items?.length > 0 && (
                            <AccommodationSection typicalAccommodationsSection={typicalAccommodationsSection} />
                        )}
                        {popularFilterListingsSections.map((section, index) => (
                            <PopularFilterListingsSection
                                key={section.heading}
                                popularFilterListingsSection={section}
                                url={url}
                                sectionId={`objects-by-popular-filters-${index + 1}`}
                                breadcrumbModel={breadcrumbListModel}
                                offerModel={offerModel}
                                suitablesModel={suitablesModel}
                            />
                        ))}
                        {isGYGEnabled && !!config.gygPartnerId && !!locationName && (
                            <GYGWrapper>
                                <GYGExperiencesSection
                                    destinationName={locationName}
                                    partnerId={config.gygPartnerId}
                                    campaign="region-landing-page"
                                />
                            </GYGWrapper>
                        )}
                        {templateData?.personInterlinksSection &&
                            templateData.personInterlinksSection.items?.length > 0 && (
                                <PersonInterlinksSection
                                    personInterlinksSection={templateData.personInterlinksSection}
                                />
                            )}
                        {!!popularFiltersSection?.popularFilters &&
                            popularFiltersSection?.popularFilters?.filters?.length > 0 && (
                                <PopularObjectSection popularFiltersSection={popularFiltersSection.popularFilters} />
                            )}
                        {quoteSection?.quote && <QuoteSection quote={quoteSection.quote} />}
                        {(regionOverviewSection?.regionOverviewList?.length > 0 ||
                            (templateData?.regionSummary?.items?.length ?? 0) > 0) && (
                            <RegionOverviewSection
                                regionOverviewSection={regionOverviewSection}
                                location={locationName}
                                regionSummary={
                                    regionOverviewSection?.regionOverviewList?.length > 0
                                        ? undefined
                                        : templateData?.regionSummary
                                }
                            />
                        )}
                        {(faqs?.length > 0 || (templateData?.faqSection?.questionAnswers?.length ?? 0) > 0) && (
                            <FAQSection
                                faqs={faqs}
                                location={locationName}
                                templateFaqSection={templateData?.faqSection || null}
                            />
                        )}
                        {tipsSection?.tips?.length > 0 && (
                            <TipsSection tipsSection={tipsSection} location={locationName} />
                        )}
                        {templateData?.keyInformation && templateData.keyInformation.items?.length > 0 && (
                            <KeyInformationSection keyInformation={templateData.keyInformation} />
                        )}
                        {!!reviewContentSection && <ReviewContentSection reviewContentSection={reviewContentSection} />}
                        {!!regionsSection && regionsSection?.regions?.length > 0 && (
                            <VacationRegionsSection regionsSection={regionsSection} />
                        )}
                        {!!holidayIdeasSection && holidayIdeasSection?.items?.length > 0 && (
                            <HolidayIdeasSection
                                holidayIdeasSection={holidayIdeasSection}
                                locationName={locationName}
                            />
                        )}

                        <NewsLetter newsletter={newsLetterSection} url={url} />
                        {hasDirectorySectionWithLinks && (
                            <DirectorySection
                                region={directoryPageResponse?.regionName || ''}
                                directoryItems={directoryPageResponse?.sections || []}
                            />
                        )}
                    </InnerContainer>
                    <FooterSection
                        footerSection={footerSection}
                        footerLogo={headerSection.navigationSectionResponse.navigation.logo}
                        socialMediaLinks={headerSection.navigationSectionResponse.socialMediaLinks}
                    />
                </Container>
            </main>
            <StickyFooter url={recommendedObjects?.cta?.url} />
        </Fragment>
    )
}

export default memo(LandingPage)
